import { Typography } from '@breetometer/components';
import { InfoIcon } from 'BreetAssets';
import { FiatInput } from 'BreetComponents';
import {
	capitalizeFirstLetter,
	cleanRate,
	convertFxRate,
	type CURRENCYCODETYPE,
	FXRATE,
	getCleanedValue,
	handleDecimalPoints,
} from 'BreetHelpers';
import { useCurrencyOutput, useCurrentFxRate, useUserCurrencyProps } from 'BreetHooks';
import { selectMarketAssetConverter, useAppSelector } from 'BreetRedux';
import { type ChangeEvent, memo, useCallback, useState } from 'react';

const FormBuyCryptoComponent = () => {
	const { isLoading } = useAppSelector(selectMarketAssetConverter({}));

	const currentFxRate = useCurrentFxRate({ fxRate: FXRATE.BUY });

	const [fiatAmount, setFiatAmount] = useState<string>(String(cleanRate(currentFxRate.rate ?? '0')));
	const [usdAmount, setUsdAmount] = useState<string>('1');

	const currencyOutput = useCurrencyOutput();

	const handleInputChange = useCallback(
		(field: 'fiat' | 'usd') => (event: ChangeEvent<HTMLInputElement>) => {
			const { value } = event.target;

			let cleanedValue = getCleanedValue(value);

			cleanedValue = handleDecimalPoints(cleanedValue);

			if (!currentFxRate.rate) return;

			if (field === 'fiat') {
				setFiatAmount(cleanedValue);
				setUsdAmount(String(convertFxRate(cleanRate(currentFxRate.rate), Number(cleanedValue), 'to')));
			}

			if (field === 'usd') {
				setUsdAmount(cleanedValue);
				setFiatAmount(String(convertFxRate(cleanRate(currentFxRate.rate), Number(cleanedValue), 'from')));
			}
		},
		[currentFxRate.rate]
	);

	const currencyProps = useUserCurrencyProps();

	return (
		<div className='rate-calculator-form'>
			<FiatInput.Default
				label={`Enter ${capitalizeFirstLetter(currencyProps?.currencyShort)} Amount`}
				currencyCode={currencyProps?.code as unknown as CURRENCYCODETYPE}
				onChange={handleInputChange('fiat')}
				value={currencyOutput(fiatAmount)}
				hideTooltip
				readOnly
				aria-readonly='true'
				loading={isLoading}
			/>

			<div className='receive-input-container'>
				<FiatInput.Default
					label='You Receive'
					currencyCode='USD'
					onChange={handleInputChange('usd')}
					value={currencyOutput(usdAmount)}
					hideTooltip
					loading={isLoading}
				/>
			</div>

			<div className='rate-calculator-note'>
				<Typography>All payouts are subject to a transaction fee.</Typography>
			</div>

			<div className='buycrypto-rate bg-primary-600 border-dark-navy'>
				<Typography className='color-primary-100'>1 USD = 1 USDT = 1 USDC</Typography>
			</div>

			<div className='buycrypto-rate-hiw'>
				<InfoIcon
					variant='mini-wallet'
					className='svg-dark'
				/>
				<Typography
					as='a'
					href='https://help.breet.io/en/articles/13769617-everything-you-need-to-know-about-the-buy-crypto-feature'
					target='_blank'
					rel='noopener noreferrer'
				>
					How it works
				</Typography>
			</div>
		</div>
	);
};

const FormBuyCrypto = memo(FormBuyCryptoComponent);

export default FormBuyCrypto;
